import { useState, useEffect, useCallback, useRef } from "react";

interface UseTimerReturn {
  secondsLeft: number;
  isRunning: boolean;
  isFinished: boolean;
  progress: number; // 0-1
  start: (fromSeconds?: number) => void;
  pause: () => void;
  reset: () => void;
}

export function useTimer(durationSeconds: number = 600): UseTimerReturn {
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const startedAtRef = useRef<number | null>(null);
  const totalDurationRef = useRef(durationSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (!startedAtRef.current) return;
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    const remaining = Math.max(0, Math.ceil(totalDurationRef.current - elapsed));
    setSecondsLeft(remaining);
    if (remaining <= 0) {
      clearTick();
      startedAtRef.current = null;
      setIsRunning(false);
      setIsFinished(true);
    }
  }, [clearTick]);

  const startInterval = useCallback(() => {
    clearTick();
    intervalRef.current = setInterval(tick, 1000);
  }, [tick, clearTick]);

  const start = useCallback((fromSeconds?: number) => {
    const dur = fromSeconds ?? durationSeconds;
    totalDurationRef.current = dur;
    startedAtRef.current = Date.now();
    setSecondsLeft(dur);
    setIsFinished(false);
    setIsRunning(true);
  }, [durationSeconds]);

  const pause = useCallback(() => {
    if (startedAtRef.current) {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      totalDurationRef.current = Math.max(0, totalDurationRef.current - elapsed);
    }
    startedAtRef.current = null;
    setIsRunning(false);
    clearTick();
  }, [clearTick]);

  const reset = useCallback(() => {
    startedAtRef.current = null;
    totalDurationRef.current = durationSeconds;
    setIsRunning(false);
    setIsFinished(false);
    setSecondsLeft(durationSeconds);
    clearTick();
  }, [durationSeconds, clearTick]);

  // Start/stop interval when isRunning changes
  useEffect(() => {
    if (!isRunning) return;
    tick(); // immediate calc
    startInterval();
    return clearTick;
  }, [isRunning, startInterval, tick, clearTick]);

  // On visibility change: restart interval + immediate tick
  useEffect(() => {
    if (!isRunning) return;
    const handleVisibility = () => {
      if (!document.hidden) {
        tick();
        startInterval();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isRunning, tick, startInterval]);

  const progress = 1 - secondsLeft / (totalDurationRef.current || durationSeconds);

  return { secondsLeft, isRunning, isFinished, progress, start, pause, reset };
}
